import React from 'react';
import ConnectorEmptyState from './ConnectorEmptyState';
import './ConnectorIntegrationsPicker.css';

export interface ConnectorIntegrationItem {
  id: string;
  url: string;
  createdAtLabel: string;
  raw: Record<string, unknown>;
}

export interface ConnectorIntegrationsPickerProps {
  integrations: ConnectorIntegrationItem[];
  selectedIntegrationId: string | null;
  onSelect: (id: string | null) => void;
  onEdit: (integration: ConnectorIntegrationItem) => void;
  onDetails: (id: string) => void;
  onCopyId: (id: string) => void;
  onDelete: (id: string) => void;
  onCreate?: () => void;
}

const ConnectorIntegrationsPicker: React.FC<ConnectorIntegrationsPickerProps> = ({
  integrations,
  selectedIntegrationId,
  onSelect,
  onEdit,
  onDetails,
  onCopyId,
  onDelete,
  onCreate,
}) => {
  return (
    <div className="connector-int-picker connector-section-panel">
      <div className="connector-int-picker__head">
        <div className="connector-int-picker__head-text">
          <h3>Your integrations</h3>
          <p>Select one to open the configurator and use other Connector tools.</p>
        </div>
        {integrations.length > 0 && (
          <span className="connector-int-picker__count">
            <i className="fas fa-plug" aria-hidden />
            {integrations.length} total
          </span>
        )}
      </div>

      {integrations.length === 0 ? (
        <div className="connector-int-picker__empty">
          <ConnectorEmptyState
            icon="fa-plug"
            title="No integrations yet"
            description="Create your first integration to connect an endpoint and start configuring agents."
            action={
              onCreate ? (
                <button
                  type="button"
                  className="connector-shell-btn connector-shell-btn--primary"
                  onClick={onCreate}
                >
                  <i className="fas fa-plus" aria-hidden />
                  Create Integration
                </button>
              ) : undefined
            }
          />
        </div>
      ) : (
        <div className="connector-int-picker__grid" id="integrations-table">
          {integrations.map((integration) => {
            const isSelected = selectedIntegrationId === integration.id;
            return (
              <article
                key={integration.id}
                className={`connector-int-card${isSelected ? ' connector-int-card--selected' : ''}`}
                aria-current={isSelected ? 'true' : undefined}
              >
                {isSelected && (
                  <span className="connector-int-card__badge" aria-label="Active integration">
                    <i className="fas fa-check" aria-hidden />
                    Active
                  </span>
                )}
                <div className="connector-int-card__top">
                  <div className="connector-int-card__icon" aria-hidden>
                    <i className="fas fa-link" />
                  </div>
                  <div className="connector-int-card__meta">
                    <p className="connector-int-card__url" title={integration.url}>
                      {integration.url}
                    </p>
                    <span className="connector-int-card__date">
                      <i className="far fa-calendar" aria-hidden />
                      {integration.createdAtLabel}
                    </span>
                  </div>
                </div>

                <div className="connector-int-card__actions">
                  <button
                    type="button"
                    className={`connector-int-card__btn${isSelected ? '' : ' connector-int-card__btn--primary'}`}
                    onClick={() => onSelect(isSelected ? null : integration.id)}
                  >
                    <i className={`fas ${isSelected ? 'fa-times' : 'fa-bullseye'}`} aria-hidden />
                    {isSelected ? 'Deselect' : 'Select'}
                  </button>
                  <button
                    type="button"
                    className="connector-int-card__btn"
                    onClick={() => onEdit(integration)}
                    disabled={!isSelected}
                    title={isSelected ? 'Edit integration' : 'Select this integration first'}
                  >
                    <i className="fas fa-edit" aria-hidden />
                    Edit
                  </button>
                  <button
                    type="button"
                    className="connector-int-card__btn"
                    onClick={() => onDetails(integration.id)}
                  >
                    <i className="fas fa-info-circle" aria-hidden />
                    Details
                  </button>
                  <button
                    type="button"
                    className="connector-int-card__btn"
                    onClick={() => onCopyId(integration.id)}
                  >
                    <i className="fas fa-copy" aria-hidden />
                    Copy ID
                  </button>
                  <button
                    type="button"
                    className="connector-int-card__btn connector-int-card__btn--danger"
                    onClick={() => onDelete(integration.id)}
                  >
                    <i className="fas fa-trash" aria-hidden />
                    Delete
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ConnectorIntegrationsPicker;
